import { Button } from "../../../components/ui/button";
import { ChevronLeft, ChevronRight, Filter, Check } from "../../../components/icons";
import { Combobox } from "../../../components/ui/combobox";
import { useLanguage } from "../../../contexts/LanguageContext";
import { apptLabels } from "../labels";
import { cn } from "../../../lib/utils";

interface AppointmentsHeaderProps {
  weekStart: Date;
  handlePrevWeek: () => void;
  handleNextWeek: () => void;
  handleGoToToday: () => void;
  setIsCreateOpen: (open: boolean) => void;
  setFiltersOpen: (open: boolean) => void;
  employeeFilter: number | "";
  setEmployeeFilter: (value: number | "") => void;
  professionalOptions: Array<{ value: string; label: string }>;
  showDone: boolean;
  setShowDone: (value: boolean | ((prev: boolean) => boolean)) => void;
  showSaturday: boolean;
  setShowSaturday: (value: boolean | ((prev: boolean) => boolean)) => void;
  showSunday: boolean;
  setShowSunday: (value: boolean | ((prev: boolean) => boolean)) => void;
  showCompanyBlocks: boolean;
  setShowCompanyBlocks: (value: boolean | ((prev: boolean) => boolean)) => void;
  showProfessionalBlocks: boolean;
  setShowProfessionalBlocks: (value: boolean | ((prev: boolean) => boolean)) => void;
  fromApp: boolean;
}

export function AppointmentsHeader({
  weekStart,
  handlePrevWeek,
  handleNextWeek,
  handleGoToToday,
  setIsCreateOpen,
  setFiltersOpen,
  employeeFilter,
  setEmployeeFilter,
  professionalOptions,
  showDone,
  setShowDone,
  showSaturday,
  setShowSaturday,
  showSunday,
  setShowSunday,
  showCompanyBlocks,
  setShowCompanyBlocks,
  showProfessionalBlocks,
  setShowProfessionalBlocks,
  fromApp,
}: AppointmentsHeaderProps) {
  const { lang } = useLanguage();
  const t = apptLabels[lang];

  return (
    <>
      {/* Mobile Header */}
      <div className="flex flex-col gap-4 lg:hidden mb-4">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-neutral-900 tracking-tight">Agenda</h1>
          <Button
            className="h-9 px-4 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-lg shadow-sm"
            onClick={() => setIsCreateOpen(true)}
          >
            {t.common.newAppointment}
          </Button>
        </div>

        <div className="flex items-center justify-between bg-white p-1.5 rounded-xl border border-neutral-200 shadow-sm">
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={handlePrevWeek}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <div className="text-[13px] font-bold text-neutral-700 min-w-[140px] text-center">
              {new Intl.DateTimeFormat("pt-BR", { day: "2-digit", month: "short" }).format(weekStart)} -{" "}
              {new Intl.DateTimeFormat("pt-BR", { day: "2-digit", month: "short" }).format(
                new Date(+weekStart + 6 * 24 * 60 * 60 * 1000)
              )}
            </div>
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={handleNextWeek}>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>

          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              className="h-8 px-2 text-[11px] font-bold uppercase text-neutral-500"
              onClick={handleGoToToday}
            >
              Hoje
            </Button>
            <div className="w-px h-4 bg-neutral-200 mx-1" />
            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0 text-neutral-600"
              onClick={() => setFiltersOpen(true)}
            >
              <Filter className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      {!fromApp && (
        <aside className="hidden lg:flex lg:col-span-12 xl:col-span-12 flex-col gap-3">
          <div className="flex flex-wrap items-center justify-between gap-4 bg-white border rounded-lg p-4">
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="icon" onClick={handlePrevWeek}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <div className="text-lg font-semibold min-w-[200px] text-center">
                {new Intl.DateTimeFormat("pt-BR", { day: "2-digit", month: "short" }).format(weekStart)} -{" "}
                {new Intl.DateTimeFormat("pt-BR", { day: "2-digit", month: "short", year: "numeric" }).format(
                  new Date(+weekStart + 6 * 24 * 60 * 60 * 1000)
                )}
              </div>
              <Button variant="ghost" size="icon" onClick={handleNextWeek}>
                <ChevronRight className="h-4 w-4" />
              </Button>
              <Button variant="outline" size="sm" onClick={handleGoToToday}>
                Hoje
              </Button>
            </div>

            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2 min-w-[250px]">
                <label className="text-sm text-neutral-700 whitespace-nowrap">
                  {t.common.professional.replace(":", "")}
                </label>
                <Combobox
                  options={professionalOptions}
                  value={employeeFilter ? String(employeeFilter) : "all"}
                  onChange={(v) => setEmployeeFilter(v === "all" ? "" : Number(v))}
                  placeholder={t.common.all}
                  searchPlaceholder={t.newPage.comboboxSearch}
                  emptyMessage={t.newPage.comboboxEmpty}
                  ariaLabel={t.common.professional.replace(":", "")}
                  className="w-full"
                />
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <button
                  onClick={() => setShowDone((v) => !v)}
                  className={cn(
                    "px-3 py-1.5 rounded-lg border text-[11px] font-bold uppercase tracking-tight transition-all flex items-center gap-1.5",
                    showDone
                      ? "border-black bg-white ring-1 ring-black text-black"
                      : "border-neutral-200 text-neutral-400 hover:border-neutral-300"
                  )}
                >
                  {showDone && <Check className="h-3 w-3" />}
                  {t.common.showDone}
                </button>

                <button
                  onClick={() => setShowSaturday((v) => !v)}
                  className={cn(
                    "px-3 py-1.5 rounded-lg border text-[11px] font-bold uppercase tracking-tight transition-all flex items-center gap-1.5",
                    showSaturday
                      ? "border-black bg-white ring-1 ring-black text-black"
                      : "border-neutral-200 text-neutral-400 hover:border-neutral-300"
                  )}
                >
                  {showSaturday && <Check className="h-3 w-3" />}
                  {t.common.showSaturday}
                </button>

                <button
                  onClick={() => setShowSunday((v) => !v)}
                  className={cn(
                    "px-3 py-1.5 rounded-lg border text-[11px] font-bold uppercase tracking-tight transition-all flex items-center gap-1.5",
                    showSunday
                      ? "border-black bg-white ring-1 ring-black text-black"
                      : "border-neutral-200 text-neutral-400 hover:border-neutral-300"
                  )}
                >
                  {showSunday && <Check className="h-3 w-3" />}
                  {t.common.showSunday}
                </button>

                <button
                  onClick={() => setShowCompanyBlocks((v) => !v)}
                  className={cn(
                    "px-3 py-1.5 rounded-lg border text-[11px] font-bold uppercase tracking-tight transition-all flex items-center gap-1.5",
                    showCompanyBlocks
                      ? "border-black bg-white ring-1 ring-black text-black"
                      : "border-neutral-200 text-neutral-400 hover:border-neutral-300"
                  )}
                >
                  {showCompanyBlocks && <Check className="h-3 w-3" />}
                  {lang === "pt" ? "Bloqueios Empresa" : "Company Blocks"}
                </button>

                <button
                  onClick={() => setShowProfessionalBlocks((v) => !v)}
                  className={cn(
                    "px-3 py-1.5 rounded-lg border text-[11px] font-bold uppercase tracking-tight transition-all flex items-center gap-1.5",
                    showProfessionalBlocks
                      ? "border-black bg-white ring-1 ring-black text-black"
                      : "border-neutral-200 text-neutral-400 hover:border-neutral-300"
                  )}
                >
                  {showProfessionalBlocks && <Check className="h-3 w-3" />}
                  {lang === "pt" ? "Bloqueios Profissionais" : "Professional Blocks"}
                </button>
              </div>
              <div className="flex items-center gap-2">
                <Button className="h-9 px-3" variant="primary" onClick={() => setIsCreateOpen(true)}>
                  {t.common.newAppointment}
                </Button>
              </div>
            </div>
          </div>
        </aside>
      )}
    </>
  );
}
